/*
 * analyticsGlossary — plain-English definitions for the metrics that appear on
 * analytics dashboards (marketing, sales, SaaS, HR/workforce, web).
 *
 * The dashboard hover "i" (GlossaryInfo) and the dashboard-level Glossary dialog
 * both use lookupMetric() below. This is intentionally content-only and generic:
 * ANY dashboard KPI whose title matches one of these metrics (now or in future)
 * automatically gets a definition — no per-dashboard wiring needed.
 *
 * NOTE: this does not touch the platform Glossary page or its API; it is an
 * additive, dashboard-side content source.
 */

// Canonical metric definitions, keyed by a normalized metric name.
export const ANALYTICS_GLOSSARY = {
    // ── Marketing / advertising ──────────────────────────────────────────
    impressions: "The number of times your ad or content was displayed on a screen, whether or not it was clicked.",
    clicks: "The number of times people clicked your ad or link.",
    ctr: "Click-through rate — clicks divided by impressions, shown as a percentage. Higher means your creative/targeting is more compelling.",
    cpc: "Cost per click — the average amount you pay each time someone clicks your ad (spend ÷ clicks).",
    cpm: "Cost per mille — the cost to show your ad 1,000 times (spend ÷ impressions × 1,000).",
    cpa: "Cost per acquisition — the average cost to win one conversion (spend ÷ conversions).",
    cpl: "Cost per lead — the average spend to generate one lead.",
    spend: "The total amount of money spent on advertising over the selected period.",
    cost: "The total amount of money spent over the selected period.",
    budget: "The planned amount allocated to spend over the period.",
    reach: "The number of unique people who saw your content at least once.",
    frequency: "The average number of times each person saw your ad (impressions ÷ reach).",
    conversions: "The number of desired actions completed — e.g. purchases, sign-ups or leads.",
    "conversion rate": "The share of visitors/clicks that completed a desired action, as a percentage.",
    roas: "Return on ad spend — revenue generated for every $1 of advertising spend (revenue ÷ spend).",
    roi: "Return on investment — net gain relative to cost, as a percentage.",
    revenue: "The total income generated over the selected period.",
    engagement: "Total interactions (likes, comments, shares, clicks) with your content.",
    "engagement rate": "Interactions divided by reach or impressions, as a percentage.",

    // ── Web / product analytics ──────────────────────────────────────────
    sessions: "The number of visits to your site or app in the period. One session can include many pageviews.",
    users: "The number of distinct people who used your product/site in the period.",
    "active users": "Users who performed a meaningful action in the period.",
    "new users": "People using your product/site for the first time in the period.",
    pageviews: "The total number of pages viewed, including repeated views.",
    "bounce rate": "The share of sessions that left after viewing only one page, as a percentage.",
    "avg session duration": "The average length of a visit.",
    aov: "Average order value — average revenue per order (revenue ÷ orders).",
    orders: "The total number of orders placed in the period.",

    // ── SaaS / revenue ───────────────────────────────────────────────────
    mrr: "Monthly recurring revenue — predictable subscription revenue normalized to a month.",
    arr: "Annual recurring revenue — MRR × 12; predictable yearly subscription revenue.",
    arpu: "Average revenue per user — revenue divided by the number of users.",
    cac: "Customer acquisition cost — the average cost to acquire one paying customer.",
    ltv: "Customer lifetime value — the total revenue expected from a customer over their relationship.",
    churn: "The rate at which customers stop paying/using, as a percentage of the base.",
    "churn rate": "The percentage of customers lost during the period.",
    retention: "The percentage of customers retained over the period.",
    "gross margin": "Revenue minus cost of goods, as a percentage of revenue.",

    // ── HR / workforce ───────────────────────────────────────────────────
    headcount: "The total number of employees at the end of the period.",
    attrition: "The number (or rate) of employees who left during the period.",
    "attrition rate": "Employees who left divided by average headcount, as a percentage.",
    turnover: "The rate at which employees leave and are replaced.",
    "turnover rate": "Separations divided by average headcount, as a percentage.",
    tenure: "The average length of time employees have been with the organization.",
    absenteeism: "The rate of unplanned employee absence.",
    "time to hire": "The average number of days from job opening to accepted offer.",
    "offer acceptance rate": "The share of job offers that candidates accept.",
    "employee count": "The total number of employees.",
    hires: "The number of new employees who joined during the period.",
    separations: "The number of employees who left during the period.",
};

// Words we can safely strip from a KPI title before matching (Total Spend → spend).
const _STRIP_PREFIX = /^(total|avg|average|overall|net|gross|no\.?\s*of|number of|#\s*of)\s+/i;

const _norm = (s) => String(s || "").trim().toLowerCase().replace(/[_-]+/g, " ").replace(/\s+/g, " ");

/**
 * Resolve a plain-English definition for a metric label/key.
 * Tries exact match, then a prefix-stripped match (e.g. "Total Impressions").
 * Returns the definition string, or null when unknown.
 */
export function lookupMetric(term, key) {
    const candidates = [];
    if (key) candidates.push(_norm(key));
    if (term) {
        const t = _norm(term);
        candidates.push(t);
        const stripped = t.replace(_STRIP_PREFIX, "").trim();
        if (stripped && stripped !== t) candidates.push(stripped);
    }
    for (const c of candidates) {
        if (ANALYTICS_GLOSSARY[c]) return ANALYTICS_GLOSSARY[c];
    }
    return null;
}
